/**
 * Operator guidance for Braze custom attribute fields (BIL-3009).
 * @module braze-configuration/attribute-field-guide
 */

import { getBillingMarketCountryOptions } from '../../billing/market-presets'
import {
  USER_LANGUAGE_DE,
  USER_LANGUAGE_EN,
  USER_LANGUAGE_ES,
  USER_LANGUAGE_FR,
} from '../../language'

export const BIL_3009_ATTRIBUTE_KEYS = [
  'user_billing_status',
  'country',
  'currency',
  'registration_language',
  'in_member_base',
  'payment_provider',
  'creation_source',
  'hold_start_date',
  'hold_end_date',
  'utm_source',
] as const

export type BrazeAttributeSuggestedOption = {
  value: string
  label?: string
  hint?: string
}

export type BrazeAttributeFieldGuideEntry = {
  tooltip?: string
  inputVariant?: 'text' | 'combobox' | 'select'
  suggestedOptions?: BrazeAttributeSuggestedOption[]
}

/** Brooklyn user signup CREATION_SOURCES plus values seen on profiles. */
export const BRAZE_CREATION_SOURCE_SUGGESTED_OPTIONS: BrazeAttributeSuggestedOption[] = [
  { value: 'zuora', label: 'zuora — web checkout' },
  { value: 'roku', label: 'roku' },
  { value: 'itunes', label: 'itunes' },
  { value: 'amazon_iap', label: 'amazon_iap' },
  { value: 'google_play', label: 'google_play' },
  { value: 'yoga_international', label: 'yoga_international' },
  { value: 'hayhouse', label: 'hayhouse' },
  { value: 'zuora_gift', label: 'zuora_gift', hint: 'Gift flows' },
  { value: 'admin', label: 'admin', hint: 'Admin-created accounts' },
]

export const BRAZE_PAYMENT_PROVIDER_SUGGESTED_OPTIONS: BrazeAttributeSuggestedOption[] = [
  { value: 'zuora', hint: 'Primary web subscription billing' },
  { value: 'stripe', hint: 'Card rail where used' },
  { value: 'itunes', hint: 'Often aligns with Apple IAP creation_source' },
  { value: 'google_play', hint: 'Often aligns with Google IAP creation_source' },
  { value: 'amazon_iap', hint: 'Amazon IAP' },
  { value: 'roku', hint: 'Roku IAP' },
]

export const BRAZE_UTM_SOURCE_SUGGESTED_OPTIONS: BrazeAttributeSuggestedOption[] = [
  { value: 'google' },
  { value: 'facebook' },
  { value: 'meta' },
  { value: 'email' },
  { value: 'organic' },
]

/** Checkout / web app locales (Brooklyn getSupportedCheckoutLanguages). */
export const REGISTRATION_LANGUAGE_SELECT_OPTIONS: BrazeAttributeSuggestedOption[] = [
  { value: USER_LANGUAGE_EN, label: 'English (en)' },
  { value: USER_LANGUAGE_ES, label: 'Español (es)' },
  { value: USER_LANGUAGE_DE, label: 'Deutsch (de)' },
  { value: USER_LANGUAGE_FR, label: 'Français (fr)' },
]

/** Brooklyn-written values plus common Braze profile labels; operators may use other strings. */
export const USER_BILLING_STATUS_SUGGESTED_OPTIONS: BrazeAttributeSuggestedOption[] = [
  {
    value: 'active',
    label: 'active',
    hint: 'Brooklyn sets on comp / resume billing events',
  },
  {
    value: 'lapsed',
    label: 'lapsed',
    hint: 'Brooklyn sets on cancel / refund-and-cancel',
  },
  {
    value: 'hold',
    label: 'hold',
    hint: 'Marketing label; pause window is usually hold_start_date / hold_end_date',
  },
  {
    value: 'suspended',
    label: 'suspended',
    hint: 'Zuora suspend (not "suspend"); verify subscription state',
  },
  {
    value: 'paused',
    label: 'paused',
    hint: 'Informal label; prefer hold dates when testing pause',
  },
]

/** Zuora ACCEPTED_CURRENCIES plus LTM (Latin America USD pricing code). */
export const BRAZE_SUPPORTED_CURRENCY_CODES = [
  'AUD',
  'ARS',
  'CAD',
  'COP',
  'EUR',
  'GBP',
  'MXN',
  'NZD',
  'LTM',
  'USD',
] as const

export const BRAZE_CURRENCY_SUGGESTED_OPTIONS: BrazeAttributeSuggestedOption[] =
  BRAZE_SUPPORTED_CURRENCY_CODES.map((code) => ({
    value: code,
    label: code === 'LTM' ? 'LTM (LatAm USD pricing code)' : code,
    ...(code === 'LTM' ? { hint: 'Not a real ISO code; used for Latin America USD checkout' } : {}),
  }))

const REGISTRATION_LANGUAGE_TOOLTIP = [
  'Braze registration_language uses short codes, not language names.',
  `Supported web app locales: ${USER_LANGUAGE_EN}, ${USER_LANGUAGE_ES}, ${USER_LANGUAGE_DE}, ${USER_LANGUAGE_FR}.`,
  'Example: use "en", not "english".',
].join(' ')

const CURRENCY_TOOLTIP = [
  'ISO 4217 codes used at checkout (Brooklyn Zuora).',
  `Common values: ${BRAZE_SUPPORTED_CURRENCY_CODES.join(', ')}.`,
  'LTM is Latin America USD pricing (not a real ISO code).',
].join(' ')

const CREATION_SOURCE_TOOLTIP = [
  'Account signup source on the user record.',
  'Brooklyn CREATION_SOURCES: roku, zuora, itunes, amazon_iap, google_play, yoga_international, hayhouse.',
  'Also seen: zuora_gift, admin.',
].join(' ')

const PAYMENT_PROVIDER_TOOLTIP = [
  'Billing/payment rail for the subscription.',
  'Common: zuora, stripe.',
  'IAP checkouts may align with creation_source (itunes, google_play, amazon_iap, roku).',
].join(' ')

const HOLD_DATE_TOOLTIP = [
  'Subscription pause (hold) window on the Braze profile.',
  'Set when subscription_paused fires; cleared on resume/renew.',
  'Maps to holdStartDate / holdEndDate on UserAttributesUpdate.',
].join(' ')

const UTM_SOURCE_TOOLTIP = [
  'Attribution utm_source (free-form).',
  'Examples from production tests: google, facebook, meta, email, organic.',
].join(' ')

const USER_BILLING_STATUS_TOOLTIP = [
  'Billing lifecycle label on the Braze profile (user_billing_status).',
  'Brooklyn explicitly sets active and lapsed on cancel/comp/resume paths.',
  'Pause is usually hold_start_date / hold_end_date; suspended is Zuora suspend (not "suspend").',
  'You can type any value already on the Braze profile.',
].join(' ')

const COUNTRY_TOOLTIP = [
  'ISO 3166-1 alpha-2 (Brooklyn billing.country).',
  'BR and other LatAm markets often bill in USD via LTM pricing — country code is still ISO-2 (e.g. BR).',
  'Pick a preset or type any code Braze has on the profile.',
].join(' ')

export const BRAZE_ATTRIBUTE_FIELD_GUIDE: Record<string, BrazeAttributeFieldGuideEntry> = {
  user_billing_status: {
    tooltip: USER_BILLING_STATUS_TOOLTIP,
    inputVariant: 'combobox',
    suggestedOptions: USER_BILLING_STATUS_SUGGESTED_OPTIONS,
  },
  country: {
    tooltip: COUNTRY_TOOLTIP,
    inputVariant: 'combobox',
    suggestedOptions: getBillingMarketCountryOptions().map((opt) => ({
      value: opt.value,
      label: opt.label,
    })),
  },
  currency: {
    tooltip: CURRENCY_TOOLTIP,
    inputVariant: 'combobox',
    suggestedOptions: BRAZE_CURRENCY_SUGGESTED_OPTIONS,
  },
  registration_language: {
    tooltip: REGISTRATION_LANGUAGE_TOOLTIP,
    inputVariant: 'select',
    suggestedOptions: REGISTRATION_LANGUAGE_SELECT_OPTIONS,
  },
  in_member_base: {},
  payment_provider: {
    tooltip: PAYMENT_PROVIDER_TOOLTIP,
    inputVariant: 'combobox',
    suggestedOptions: BRAZE_PAYMENT_PROVIDER_SUGGESTED_OPTIONS,
  },
  creation_source: {
    tooltip: CREATION_SOURCE_TOOLTIP,
    inputVariant: 'combobox',
    suggestedOptions: BRAZE_CREATION_SOURCE_SUGGESTED_OPTIONS,
  },
  hold_start_date: {
    tooltip: HOLD_DATE_TOOLTIP,
  },
  hold_end_date: {
    tooltip: HOLD_DATE_TOOLTIP,
  },
  utm_source: {
    tooltip: UTM_SOURCE_TOOLTIP,
    inputVariant: 'combobox',
    suggestedOptions: BRAZE_UTM_SOURCE_SUGGESTED_OPTIONS,
  },
}

export type BrazeFormFieldWithGuide = {
  brazeKey: string
  tooltip?: string
  inputVariant?: 'text' | 'combobox' | 'select'
  suggestedOptions?: BrazeAttributeSuggestedOption[]
}

/**
 * Merges BIL-3009 guide metadata onto base form field defs (single source for tooltips/options).
 */
export function applyAttributeFieldGuide<T extends { brazeKey: string }> (
  fields: T[],
): Array<T & BrazeFormFieldWithGuide> {
  return fields.map((field) => {
    const guide = BRAZE_ATTRIBUTE_FIELD_GUIDE[field.brazeKey]
    if (!guide) {
      return field
    }
    return {
      ...field,
      ...(guide.tooltip ? { tooltip: guide.tooltip } : {}),
      ...(guide.inputVariant ? { inputVariant: guide.inputVariant } : {}),
      ...(guide.suggestedOptions ? { suggestedOptions: guide.suggestedOptions } : {}),
    }
  })
}
